import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";
import { fmtUSD, fmtInt } from "../../lib/analytics";
import { formatPhone } from "../../lib/phoneFormat";
import { useNavigate, useLocation } from "react-router-dom";
import DealAgeChips from "../shared/DealAgeChips";

// ---------------------------------------------------------------------------
// Session persistence for phase/stage/scroll (survives back-navigation)
// ---------------------------------------------------------------------------
const PHASE_KEY = "pipeline_phase_state";
const SCROLL_KEY = "pipeline_scroll_y";

function savePipelineState(phase: string, stage: string | null) {
  try { sessionStorage.setItem(PHASE_KEY, JSON.stringify({ phase, stage })); } catch {}
}
function loadPipelineState(): { phase: string; stage: string | null } | null {
  try {
    const raw = sessionStorage.getItem(PHASE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveScrollPos() {
  try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch {}
}
function loadScrollPos(): number | null {
  try {
    const v = sessionStorage.getItem(SCROLL_KEY);
    return v ? parseInt(v, 10) : null;
  } catch { return null; }
}
import {
  type PipelinePhase,
  PIPELINE_PHASES,
  PHASE_LABELS,
  PHASE_COLORS,
  PHASE_STAGES,
  STAGE_LABELS,
} from "./stageMap";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { heicToJpeg } from "../../lib/heicToJpeg";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { GATED_TARGETS, SUBMITTED_STAGES, fetchCurrentPlans, firstPaymentStatus } from "../../lib/buildPaymentPlan";
import { passToNotifyPlan, useBuildSubmitGate } from "../deal/BuildPaymentPlanModal";
// getNextAction removed — replaced by TransitionButton
import DealCallText from "../DealCallText";
import TransitionButton from "../deal/TransitionButton";
import { useAuth } from "../../context/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PipelineDeal = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "homeowner_phone"
  | "property_street"
  | "property_city"
  | "property_zip"
  | "claim_number"
  | "insurance_company"
  | "claim_notes"
  | "shingle_color"
  | "stage"
  | "phase"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
  | "acv_amount"
  | "deductible"
  | "commissionable_revenue"
  | "owner_id"
  | "split_rm_id"
  | "adjuster_meeting_date"
  | "adjuster_name"
  | "pre_inspection_date"
  | "pre_inspection_assigned_to"
  | "pre_inspection_completed"
  | "assigned_rs_id"
  | "build_scheduled_date"
  | "ho_preferred_build_date"
  | "build_completed_date"
  | "material_order_date"
  | "updated_at"
  | "supplement_status"
  | "supplement_approved_amount"
  | "supplement_submitted_to_insurance_date"
  | "supplement_approved_date"
  | "pre_build_amount_received"
  | "post_build_amount_received"
  | "final_invoice_amount"
  | "total_contract_amount"
  | "receivable_status"
  | "scope_parsed"
  | "scope_confirmed"
  | "asm_review_status"
  | "report_qc_status"
  | "claims_expectation_flag"
  | "withdraw_status"
  | "withdraw_reason"
  | "presentation_type"
  | "insurance_paperwork_uploaded"
  | "reinspection_adjuster_meeting_date"
  | "reinspection_partial_approval"
>;

interface PipelineViewProps {
  /** Filter deals to specific owner(s). null = all deals (exec view). */
  ownerIds?: string[] | null;
  /** Show the Closing tab? (only for Chad, Jennifer, Greg) */
  showClosing?: boolean;
  /** Current user's team member ID (for split deal detection) */
  myId?: string;
  /** Show owner name on cards? (for TL/ASM/exec views showing multiple RMs) */
  showOwner?: boolean;
  /** Map of team member IDs to names */
  members?: Record<string, { full_name: string }>;
  /** Current user's role — enables mass update for admin, sales_director, qc, asm, team_lead */
  userRole?: string;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

// Roles allowed to use mass update, stage changes via card menu, and RM reassignment
// roof_director kept alongside director_of_operations for back-compat (Apr 28 dual-role policy)
const MASS_UPDATE_ROLES = ["admin", "sales_director", "asm", "director_of_operations", "roof_director"];
// Roles that get the "Waiting" quick button on lead cards (Greg Oct 5, 2026: TL, ASM, RM)
const WAITING_BUTTON_ROLES = ["restore_manager", "team_lead", "asm", "admin", "sales_director"];

export default function PipelineView({
  ownerIds,
  showClosing = false,
  myId,
  showOwner = false,
  members,
  userRole,
}: PipelineViewProps) {
  const [deals, setDeals] = useState<PipelineDeal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { isSuperUser, member } = useAuth();

  // Mass update state
  const canMassUpdate = isSuperUser || (!!userRole && MASS_UPDATE_ROLES.includes(userRole));
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [massStage, setMassStage] = useState("");
  const [massUpdating, setMassUpdating] = useState(false);
  const savedPipeState = useRef(loadPipelineState());
  const [activePhase, setActivePhase] = useState<PipelinePhase>(
    (savedPipeState.current?.phase as PipelinePhase) ?? "leads",
  );
  const [activeStage, setActiveStage] = useState<string | null>(
    savedPipeState.current?.stage ?? null,
  );
  const [loadVersion, setLoadVersion] = useState(0);
  const reload = useCallback(() => setLoadVersion((v) => v + 1), []);
  const restoredScroll = useRef(false);

  // Persist phase/stage to sessionStorage
  useEffect(() => {
    savePipelineState(activePhase, activeStage);
  }, [activePhase, activeStage]);

  // Restore scroll position after deals load — use multiple rAF + timeout
  // to ensure React has actually rendered the deal cards before scrolling
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null;
    if (deals && deals.length > 0 && !restoredScroll.current) {
      restoredScroll.current = true;
      const y = loadScrollPos();
      if (y !== null && y > 0) {
        // Double-rAF ensures the browser has painted after React commit
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, y);
            // Safety net: if layout shift happened, retry after a brief delay
            timerId = setTimeout(() => {
              if (Math.abs(window.scrollY - y) > 50) {
                window.scrollTo(0, y);
              }
            }, 150);
          });
        });
      }
    }
    return () => {
      if (timerId !== null) clearTimeout(timerId);
    };
  }, [deals]);

  // Which phases to show
  const phases = useMemo(
    () => (showClosing ? PIPELINE_PHASES : PIPELINE_PHASES.filter((p) => p !== "closing")),
    [showClosing],
  );

  useEffect(() => {
    let cancelled = false;

    const COLS = [
      "id", "homeowner_name", "homeowner_phone", "property_street", "property_city", "property_zip",
      "claim_number", "insurance_company", "claim_notes", "shingle_color",
      "stage", "phase", "days_in_stage", "stage_entered_at", "phase_entered_at", "created_at", "acv_amount", "deductible", "commissionable_revenue",
      "owner_id", "split_rm_id",
      "adjuster_meeting_date", "adjuster_name", "pre_inspection_date", "pre_inspection_assigned_to",
      "pre_inspection_completed", "assigned_rs_id", "reinspection_adjuster_meeting_date", "reinspection_partial_approval",
      "build_scheduled_date", "ho_preferred_build_date", "build_completed_date", "material_order_date",
      "updated_at",
      "supplement_status", "supplement_approved_amount", "supplement_submitted_to_insurance_date",
      "supplement_approved_date",
      "pre_build_amount_received", "post_build_amount_received",
      "final_invoice_amount", "total_contract_amount", "receivable_status",
      "scope_parsed", "scope_confirmed", "asm_review_status", "report_qc_status",
      "claims_expectation_flag", "withdraw_status", "withdraw_reason",
      "presentation_type", "insurance_paperwork_uploaded",
    ].join(",");

    // Build a fresh query chain per iteration of the pagination loop below.
    // supabase-js builders mutate URL params on chained calls (.range / .order
    // overwrite, but .or appends), so rebuilding each iteration is the safest
    // pattern — same approach as the May 18, 2026 Canvas.tsx bbox fix.
    function buildQuery() {
      let q = supabase
        .from("deals")
        .select(COLS)
        .not("stage", "in", "(closed_won,closed_lost,cancelled)");
      if (ownerIds && ownerIds.length > 0) {
        if (ownerIds.length === 1) {
          q = q.or(`owner_id.eq.${ownerIds[0]},split_rm_id.eq.${ownerIds[0]}`);
        } else {
          const orClauses = ownerIds.map((id) => `owner_id.eq.${id},split_rm_id.eq.${id}`).join(",");
          q = q.or(orClauses);
        }
      }
      // Exclude drone-phase stages — those deals live in Drone Ops / ASM Inbox,
      // not the RM pipeline. They enter the pipeline after report_approved → leads.
      q = q.not(
        "stage",
        "in",
        "(pending_asm_review,appointment_received,drone_scheduled,drone_complete,report_in_progress,qc_review,report_approved,needs_climb_inspection,insufficient_damage,closed_inspection)",
      );
      return q;
    }

    async function load() {
      // PostgREST hard-caps at 1000 rows per query. The original `.limit(10000)`
      // with comment "Override Supabase default 1000-row limit" was a no-op —
      // exec/admin pipeline views were silently truncated. With 3,221 active
      // pipeline deals (May 18, 2026), ~31% of the pipeline was invisible.
      // Paginate via .range() in 1000-row chunks; bail on partial chunk = last
      // page. Hard ceiling at 20K so a future bug can't infinite-loop.
      const CHUNK = 1000;
      const SAFETY_CAP = 20000;
      const all: PipelineDeal[] = [];
      for (let from = 0; from < SAFETY_CAP; from += CHUNK) {
        const { data, error } = await buildQuery()
          .order("stage_entered_at", { ascending: true })
          .range(from, from + CHUNK - 1);
        if (cancelled) return;
        if (error) {
          setErr(error.message);
          return;
        }
        const chunk = (data ?? []) as unknown as PipelineDeal[];
        all.push(...chunk);
        if (chunk.length < CHUNK) break;
      }
      if (!cancelled) setDeals(all);
    }
    load();
    return () => { cancelled = true; };
  }, [ownerIds, loadVersion]);

  // Count deals per phase (exclude inactive stages from badge counts)
  const INACTIVE_STAGE_SET = new Set(["no_thanks", "claims_denied"]);
  const phaseCounts = useMemo(() => {
    if (!deals) return {};
    const counts: Record<string, number> = {};
    for (const d of deals) {
      if (INACTIVE_STAGE_SET.has(d.stage ?? "")) continue;
      const p = d.phase ?? "leads";
      counts[p] = (counts[p] || 0) + 1;
    }
    return counts;
  }, [deals]);

  // Deals for active phase, optionally filtered by stage
  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    const phaseDeals = deals.filter((d) => (d.phase ?? "leads") === activePhase);
    if (!activeStage) return phaseDeals;
    return phaseDeals.filter((d) => d.stage === activeStage);
  }, [deals, activePhase, activeStage]);

  // Stage counts within active phase
  const stageCounts = useMemo(() => {
    if (!deals) return {};
    const phaseDeals = deals.filter((d) => (d.phase ?? "leads") === activePhase);
    const counts: Record<string, number> = {};
    for (const d of phaseDeals) {
      const s = d.stage ?? "unknown";
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [deals, activePhase]);

  // Reset stage filter when phase changes (skip initial mount if restoring)
  const phaseChangeCount = useRef(0);
  useEffect(() => {
    phaseChangeCount.current++;
    // Don't reset on first render (restored from session) — only on user clicks
    if (phaseChangeCount.current > 1) {
      setActiveStage(null);
    }
  }, [activePhase]);

  // Auto-select first phase that has deals — only if no saved state
  const [initialPhasePicked, setInitialPhasePicked] = useState(false);
  useEffect(() => {
    if (!deals || initialPhasePicked) return;
    // If we restored from session, keep that phase
    if (savedPipeState.current?.phase) {
      setInitialPhasePicked(true);
      return;
    }
    const firstWithDeals = phases.find((p) => (phaseCounts[p] || 0) > 0);
    if (firstWithDeals) {
      setActivePhase(firstWithDeals);
    }
    setInitialPhasePicked(true);
  }, [deals, phaseCounts, phases, initialPhasePicked]);

  // Toggle a deal's selection
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Select / deselect all visible deals
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredDeals.length && filteredDeals.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDeals.map((d) => d.id)));
    }
  }, [filteredDeals, selectedIds.size]);

  // Exit select mode
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setMassStage("");
  }, []);

  // All stages grouped by phase for the mass update dropdown
  const MASS_STAGE_OPTIONS: { phase: string; stages: string[] }[] = useMemo(() => [
    { phase: "Drone", stages: ["pending_asm_review", "appointment_received", "drone_scheduled", "drone_complete", "report_in_progress", "qc_review", "report_approved", "needs_climb_inspection", "insufficient_damage"] },
    { phase: "Leads", stages: PHASE_STAGES.leads },
    { phase: "Claims", stages: PHASE_STAGES.claims },
    { phase: "Reinspections", stages: PHASE_STAGES.reinspections },
    { phase: "Builds", stages: PHASE_STAGES.builds },
    { phase: "Closing", stages: PHASE_STAGES.closing },
    { phase: "Closed", stages: ["closed_won", "closed_lost", "cancelled"] },
  ], []);

  // Mass stage update handler
  const handleMassUpdate = useCallback(async () => {
    if (!massStage || selectedIds.size === 0) return;
    const count = selectedIds.size;
    const stageLabel = STAGE_LABELS[massStage] ?? massStage;
    if (!window.confirm(`Move ${count} deal${count > 1 ? "s" : ""} to "${stageLabel}"?`)) return;

    setMassUpdating(true);
    try {
      // Moving deals into the build stages submits their builds (Oct 21, 2026):
      // each one with the first payment not captured needs its own plan, so a
      // bulk move stops and names them instead of failing half way.
      if (GATED_TARGETS.has(massStage)) {
        const crossing = (deals ?? []).filter((dl) => selectedIds.has(dl.id) && !SUBMITTED_STAGES.has(dl.stage ?? ""));
        if (crossing.length > 0) {
          const ids = crossing.map((dl) => dl.id);
          const [{ data: full, error: fullErr }, plans] = await Promise.all([
            supabase.from("deals").select("*").in("id", ids),
            fetchCurrentPlans(ids),
          ]);
          if (fullErr) throw fullErr;
          const missing = ((full ?? []) as Record<string, unknown>[])
            .filter((row) => !firstPaymentStatus(row).isCaptured && !plans[String(row.id)])
            .map((row) => String(row.homeowner_name ?? "(no name)"));
          if (missing.length > 0) {
            alert(
              `These builds need a first payment plan before they can be submitted, so nothing was moved:\n\n` +
              missing.map((m) => `• ${m}`).join("\n") +
              `\n\nSubmit them one at a time from their cards; each asks how the first payment will be collected.`,
            );
            return;
          }
        }
      }
      // Determine new phase
      let newPhase: string | null = null;
      for (const [ph, stages] of Object.entries(PHASE_STAGES)) {
        if (stages.includes(massStage)) { newPhase = ph; break; }
      }
      const droneStages = ["pending_asm_review", "appointment_received", "drone_scheduled", "drone_complete", "report_in_progress", "qc_review", "report_approved", "needs_climb_inspection", "insufficient_damage"];
      if (droneStages.includes(massStage)) newPhase = "drone";
      if (["closed_won", "closed_lost", "cancelled"].includes(massStage)) newPhase = "closed";

      const update: Record<string, unknown> = { stage: massStage, days_in_stage: 0 };
      if (newPhase) update.phase = newPhase;

      // Batch update all selected deals
      const ids = Array.from(selectedIds);
      const { error } = await supabase
        .from("deals")
        .update(update)
        .in("id", ids);

      if (error) throw error;
      // Mass-moving deals into Chad's build-approval queue — fire the
      // SMS + in-app notification per deal (skip ones already there).
      if (massStage === "build_submitted") {
        for (const dl of deals ?? []) {
          if (selectedIds.has(dl.id) && dl.stage !== "build_submitted") {
            void notifyChadOnBuildApproval(dl, { actorId: member?.id });
          }
        }
      }
      exitSelectMode();
      reload();
    } catch (err: any) {
      alert(`Mass update failed: ${err.message ?? err}`);
    } finally {
      setMassUpdating(false);
    }
  }, [massStage, selectedIds, exitSelectMode, reload, deals, member?.id]);

  // Stages that are effectively "closed" — still visible in pipeline tabs for
  // rework but excluded from active deal count and pipeline value.
  const activeDeals = useMemo(
    () => (deals ?? []).filter((d) => !INACTIVE_STAGE_SET.has(d.stage ?? "")),
    [deals],
  );
  const totalActive = activeDeals.length;
  const totalValue = activeDeals.reduce(
    (s, d) => s + (Number(d.acv_amount) || 0) + (Number(d.deductible) || 0),
    0,
  );

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm">{err}</div>;
  if (!deals) return <div className="text-slate-500 py-8 text-center text-sm">Loading pipeline…</div>;

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow px-4 py-3">
        <div>
          <span className="text-lg font-semibold text-bardin-ink">{fmtInt(totalActive)}</span>
          <span className="text-sm text-slate-500 ml-1">active deals</span>
        </div>
        <div className="flex items-center gap-3">
          {canMassUpdate && (
            <button
              onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
              className={
                "text-xs font-medium px-3 py-1.5 rounded-lg transition " +
                (selectMode
                  ? "bg-bardin-ink text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200")
              }
            >
              {selectMode ? "Cancel Select" : "Select"}
            </button>
          )}
          <div className="text-right">
            <span className="text-lg font-semibold text-bardin-ink">{fmtUSD(totalValue, { short: true })}</span>
            <span className="text-sm text-slate-500 ml-1">pipeline</span>
          </div>
        </div>
      </div>

      {/* Phase tabs — horizontal scroll on mobile */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1.5 min-w-max">
          {phases.map((phase) => {
            const count = phaseCounts[phase] || 0;
            const isActive = activePhase === phase;
            return (
              <button
                key={phase}
                onClick={() => setActivePhase(phase)}
                className={
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap " +
                  (isActive
                    ? "bg-bardin-ink text-white shadow-sm"
                    : "bg-white text-slate-600 hover:bg-slate-50 shadow-sm")
                }
              >
                <span
                  className={
                    "w-2 h-2 rounded-full " +
                    (isActive ? "bg-white" : PHASE_COLORS[phase])
                  }
                />
                {PHASE_LABELS[phase]}
                <span
                  className={
                    "text-xs px-1.5 py-0.5 rounded-full font-semibold " +
                    (isActive
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500")
                  }
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stage filter pills */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1.5 min-w-max">
          <button
            onClick={() => setActiveStage(null)}
            className={
              "px-2.5 py-1 rounded-full text-xs font-medium transition " +
              (!activeStage
                ? "bg-bardin-teal text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            All ({phaseCounts[activePhase] || 0})
          </button>
          {PHASE_STAGES[activePhase].map((stage) => {
            const count = stageCounts[stage] || 0;
            const isActive = activeStage === stage;
            return (
              <button
                key={stage}
                onClick={() => setActiveStage(isActive ? null : stage)}
                className={
                  "px-2.5 py-1 rounded-full text-xs font-medium transition whitespace-nowrap " +
                  (isActive
                    ? "bg-bardin-teal text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                }
              >
                {STAGE_LABELS[stage] ?? stage} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Deal cards */}
      <div className="space-y-2">
        {filteredDeals.length === 0 && (
          <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
            No deals in {PHASE_LABELS[activePhase]}
            {activeStage ? ` → ${STAGE_LABELS[activeStage] ?? activeStage}` : ""}.
          </div>
        )}

        {filteredDeals.map((d) => (
          <DealCard
            key={d.id}
            deal={d}
            phase={activePhase}
            myId={myId}
            showOwner={showOwner}
            members={members}
            userRole={userRole}
            onMutate={reload}
            selectMode={selectMode}
            isSelected={selectedIds.has(d.id)}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>

      {/* Floating mass update bar */}
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg px-4 py-3 safe-area-bottom">
          <div className="max-w-xl mx-auto flex items-center gap-2 flex-wrap">
            <button
              onClick={toggleSelectAll}
              className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
            >
              {selectedIds.size === filteredDeals.length && filteredDeals.length > 0 ? "Deselect All" : "Select All"}
            </button>
            <span className="text-sm font-semibold text-bardin-ink">
              {selectedIds.size} selected
            </span>
            <select
              value={massStage}
              onChange={(e) => setMassStage(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 flex-1 min-w-[140px] max-w-[200px]"
            >
              <option value="">Move to…</option>
              {MASS_STAGE_OPTIONS.map((group) => (
                <optgroup key={group.phase} label={group.phase}>
                  {group.stages.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABELS[s] ?? s}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              onClick={handleMassUpdate}
              disabled={!massStage || selectedIds.size === 0 || massUpdating}
              className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-bardin-teal text-white hover:bg-bardin-teal/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {massUpdating ? "Updating…" : "Apply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Phone display now sourced from `lib/phoneFormat.ts` (shared with all other
// staff-facing display sites). The local fmtPhone() was removed Apr 28, 2026
// alongside the homeowner_phone E.164 backfill.

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Small chip component */
function Chip({ children, color = "slate" }: { children: React.ReactNode; color?: string }) {
  const colors: Record<string, string> = {
    slate: "bg-slate-100 text-slate-600",
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
    teal: "bg-teal-50 text-teal-700",
    purple: "bg-purple-50 text-purple-600",
  };
  return (
    <span className={"text-[10px] font-medium px-1.5 py-0.5 rounded " + (colors[color] || colors.slate)}>
      {children}
    </span>
  );
}

/** Detail line (label: value) */
function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="text-xs text-slate-500 truncate">
      <span className="text-slate-400">{label}:</span> {value}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deal Card — adapts content per phase
// ---------------------------------------------------------------------------

function DealCard({
  deal: d,
  phase,
  myId,
  showOwner,
  members,
  userRole,
  onMutate,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
}: {
  deal: PipelineDeal;
  phase: PipelinePhase;
  myId?: string;
  showOwner?: boolean;
  members?: Record<string, { full_name: string }>;
  userRole?: string;
  onMutate?: () => void;
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperUser, member } = useAuth();
  const [showActions, setShowActions] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stageUpdating, setStageUpdating] = useState(false);
  // Moving a deal into the build stages from the dropdown submits its build:
  // ask how the first payment will be collected when it isn't captured.
  const gate = useBuildSubmitGate();
  // CompanyCam upload for reinspections cards
  const [ccUploading, setCcUploading] = useState(false);
  const [ccDragOver, setCcDragOver] = useState(false);
  const ccFileRef = useRef<HTMLInputElement>(null);
  const isSplit = myId ? (d.split_rm_id === myId || d.owner_id !== myId) : !!d.split_rm_id;
  const ownerName = showOwner && d.owner_id && members?.[d.owner_id]?.full_name;
  const splitRmName = d.split_rm_id && members?.[d.split_rm_id]?.full_name;
  const stageLabel = STAGE_LABELS[d.stage ?? ""] ?? d.stage ?? "—";
  const rsName = d.assigned_rs_id && members?.[d.assigned_rs_id]?.full_name;

  // Address line: street, city, zip
  const addrParts = [d.property_street, d.property_city, d.property_zip].filter(Boolean);
  const addressLine = addrParts.length > 0 ? addrParts.join(", ") : "—";

  // All stages grouped by phase for the dropdown
  const ALL_STAGE_OPTIONS: { phase: string; stages: string[] }[] = [
    { phase: "Drone", stages: ["pending_asm_review", "appointment_received", "drone_scheduled", "drone_complete", "report_in_progress", "qc_review", "report_approved", "needs_climb_inspection", "insufficient_damage"] },
    { phase: "Leads", stages: PHASE_STAGES.leads },
    { phase: "Claims", stages: PHASE_STAGES.claims },
    { phase: "Reinspections", stages: PHASE_STAGES.reinspections },
    { phase: "Builds", stages: PHASE_STAGES.builds },
    { phase: "Closing", stages: PHASE_STAGES.closing },
    { phase: "Closed", stages: ["closed_won", "closed_lost", "cancelled"] },
  ];

  const canDeleteDeal = !!userRole && ["admin", "sales_director", "asm"].includes(userRole);
  // "Waiting" quick button on lead cards — RM, TL, ASM (+ leadership)
  const canSetWaiting = isSuperUser || (!!userRole && WAITING_BUTTON_ROLES.includes(userRole));

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${d.homeowner_name ?? "(no name)"}" permanently? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      // All child FK references cascade on delete — just delete the deal
      const { error } = await supabase.from("deals").delete().eq("id", d.id);
      if (error) throw error;
      onMutate?.();
    } catch (err: any) {
      alert(`Delete failed: ${err.message ?? err}`);
    } finally {
      setDeleting(false);
    }
  }

  async function handleStageChange(e: React.ChangeEvent<HTMLSelectElement>) {
    e.stopPropagation();
    const newStage = e.target.value;
    if (!newStage || newStage === d.stage) return;
    setStageUpdating(true);
    try {
      // Determine new phase from stage
      let newPhase: string | null = null;
      for (const [ph, stages] of Object.entries(PHASE_STAGES)) {
        if (stages.includes(newStage)) { newPhase = ph; break; }
      }
      // Handle drone/closed phases not in PHASE_STAGES
      const droneStages = ["pending_asm_review", "appointment_received", "drone_scheduled", "drone_complete", "report_in_progress", "qc_review", "report_approved", "needs_climb_inspection", "insufficient_damage"];
      if (droneStages.includes(newStage)) newPhase = "drone";
      if (["closed_won", "closed_lost", "cancelled"].includes(newStage)) newPhase = "closed";

      const update: Record<string, unknown> = { stage: newStage, days_in_stage: 0 };
      if (newPhase) update.phase = newPhase;

      const pass = await gate.run(
        { dealId: d.id, homeowner: d.homeowner_name, fromStage: d.stage, toStage: newStage, source: "pipeline", title: "Submit build", actionLabel: `Move to ${STAGE_LABELS[newStage] ?? newStage}` },
        async () => {
          const { error } = await supabase.from("deals").update(update).eq("id", d.id);
          if (error) throw error;
        },
      );
      if (!pass) return; // cancelled
      // Text Chad when a deal lands in his build-approval queue via the
      // pipeline stage dropdown. Same fire-and-forget helper as
      // TransitionButton / AdminControlsPanel / RoofMasterTable — an audit
      // (Jun 11, 2026) found this path was the main route into
      // build_submitted and was silently skipping the alert.
      if (newStage === "build_submitted" && d.stage !== "build_submitted") {
        void notifyChadOnBuildApproval(d, { actorId: member?.id, plan: passToNotifyPlan(pass) });
      }
      onMutate?.();
    } catch (err: any) {
      alert(`Stage update failed: ${err.message ?? err}`);
    } finally {
      setStageUpdating(false);
    }
  }

  // "Waiting on" approval flags — tells RM who they're blocked on
  const waitingFlags: string[] = [];
  if (d.stage === "pending_asm_review" || d.asm_review_status === "pending") {
    waitingFlags.push("Waiting on Alex (review)");
  }
  if (d.stage === "build_submitted") {
    waitingFlags.push("Waiting on build approval (Chad or Robert)");
  }
  if (d.scope_parsed && !d.scope_confirmed) {
    waitingFlags.push("Waiting on Chad (scope approval)");
  }
  if (d.report_qc_status === "pending" || d.stage === "qc_review") {
    waitingFlags.push("Waiting on Jennifer (QC)");
  }
  if (d.stage === "waiting_on_claims") {
    waitingFlags.push("Waiting on insurance");
  }
  // Claims phase: flag if adjuster meeting not yet scheduled
  const CLAIMS_STAGES = ["assign_roof_specialist", "pre_inspection_ready", "adjuster_meeting_ready"];
  if (CLAIMS_STAGES.includes(d.stage ?? "") && !d.adjuster_meeting_date) {
    waitingFlags.push("Need to schedule adjuster meeting");
  }

  // CompanyCam upload helpers (reinspections)
  async function handleCcUpload(files: File[]) {
    setCcUploading(true);
    try {
      for (const picked of files) {
        // Convert iPhone HEIC/HEIF to JPEG so reinspection photos render in
        // every browser. No-op for PDFs/JPEGs (the common cases here).
        const file = await heicToJpeg(picked);
        const ext = file.name.split(".").pop() ?? "pdf";
        const path = `deals/${d.id}/companycam_report_${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("deal-documents")
          .upload(path, file, {
            upsert: true,
            contentType: file.type || "application/octet-stream",
          });
        if (uploadErr) throw uploadErr;
        const { error: insertErr } = await supabase.from("deal_documents").insert({
          deal_id: d.id,
          file_name: file.name,
          file_type: "companycam_report",
          storage_path: path,
          file_size: file.size,
          mime_type: file.type || null,
          uploaded_by: null,
        });
        if (insertErr) throw insertErr;
        await supabase.from("activity_log").insert({
          deal_id: d.id,
          activity_type: "document_upload",
          title: "CompanyCam Report uploaded",
          description: file.name,
          metadata: { storage_path: path, doc_type: "companycam_report" },
        });
      }
      alert(`CompanyCam Report uploaded.`);
      onMutate?.();
    } catch (e: any) {
      alert(`Upload failed: ${e.message ?? e}`);
    } finally {
      setCcUploading(false);
    }
  }

  return (
    <>
    {gate.element}
    <div
      className={
        "bg-white rounded-xl shadow px-4 py-3 space-y-1.5 cursor-pointer hover:shadow-md transition " +
        (selectMode && isSelected ? "ring-2 ring-bardin-teal bg-bardin-sky/10" : "hover:bg-slate-50/50")
      }
      onClick={() => {
        if (selectMode) { onToggleSelect?.(d.id); return; }
        saveScrollPos(); navigate(`/deal/${d.id}`, { state: { from: location.pathname + location.search } });
      }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          if (selectMode) { onToggleSelect?.(d.id); return; }
          saveScrollPos(); navigate(`/deal/${d.id}`, { state: { from: location.pathname + location.search } });
        }
      }}
    >
      {/* Row 1: Name + stage/days */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 flex items-start gap-2">
          {selectMode && (
            <div className="flex-shrink-0 pt-0.5" onClick={(e) => { e.stopPropagation(); onToggleSelect?.(d.id); }}>
              <div
                className={
                  "w-5 h-5 rounded border-2 flex items-center justify-center transition " +
                  (isSelected
                    ? "bg-bardin-teal border-bardin-teal"
                    : "border-slate-300 bg-white hover:border-bardin-teal/50")
                }
              >
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          )}
          <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-sm text-bardin-ink">
              {d.homeowner_name ?? "(no name)"}
            </span>
            {isSplit && <Chip color="indigo">Split</Chip>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {isAddressLinkable(d) ? (
              <a
                href={mapsHref(d)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hover:underline hover:text-slate-700"
              >
                {addressLine}
              </a>
            ) : (
              addressLine
            )}
          </div>
          {ownerName && (
            <div className="text-xs text-slate-400 mt-0.5">
              RM: {ownerName}
              {isSplit && splitRmName && <> &amp; {splitRmName}</>}
            </div>
          )}
          {!ownerName && isSplit && splitRmName && (
            <div className="text-xs text-slate-400 mt-0.5">
              Split with: {splitRmName}
            </div>
          )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {stageLabel}
          </span>
          <DealAgeChips deal={d} align="right" className="mt-1" />
        </div>
      </div>

      {/* Waiting-on flags */}
      {waitingFlags.length > 0 && (
        <div className="flex flex-col gap-1">
          {waitingFlags.map((flag) => (
            <div
              key={flag}
              className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5"
            >
              <span className="text-amber-500 text-sm">&#9203;</span>
              <span className="text-xs font-medium text-amber-700">{flag}</span>
            </div>
          ))}
        </div>
      )}

      {/* Phase-specific content */}
      {phase === "leads" && (
        <>
          <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
            {d.homeowner_phone && <span>{formatPhone(d.homeowner_phone)}</span>}
            {d.insurance_company && <Chip color="purple">{d.insurance_company}</Chip>}
            {d.updated_at && (
              <span className="text-slate-400">Last action: {timeAgo(d.updated_at)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {canSetWaiting && d.stage !== "waiting" && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Move "${d.homeowner_name ?? "(no name)"}" to Waiting?`)) return;
                  setStageUpdating(true);
                  try {
                    const { error } = await supabase
                      .from("deals")
                      .update({ stage: "waiting", phase: "leads", days_in_stage: 0 })
                      .eq("id", d.id);
                    if (error) throw error;
                    onMutate?.();
                  } catch (err: any) {
                    alert(`Update failed: ${err.message ?? err}`);
                  } finally {
                    setStageUpdating(false);
                  }
                }}
                disabled={stageUpdating}
                className="px-3 py-1 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                {stageUpdating ? "Updating…" : "Waiting"}
              </button>
            )}
            {d.stage !== "no_thanks" && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Move "${d.homeowner_name ?? "(no name)"}" to No Thanks?`)) return;
                  setStageUpdating(true);
                  try {
                    const { error } = await supabase
                      .from("deals")
                      .update({ stage: "no_thanks", phase: "leads", days_in_stage: 0 })
                      .eq("id", d.id);
                    if (error) throw error;
                    onMutate?.();
                  } catch (err: any) {
                    alert(`Update failed: ${err.message ?? err}`);
                  } finally {
                    setStageUpdating(false);
                  }
                }}
                disabled={stageUpdating}
                className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {stageUpdating ? "Updating…" : "No Thanks"}
              </button>
            )}
          </div>
        </>
      )}

      {(phase === "claims" || phase === "reinspections") && (
        <>
          <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
            {d.homeowner_phone && <span>{formatPhone(d.homeowner_phone)}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {rsName && <Chip color="teal">RS: {rsName.split(" ")[0]}</Chip>}
            {d.insurance_company && <Chip color="purple">{d.insurance_company}</Chip>}
            {d.claim_number && <Chip color="slate">#{d.claim_number}</Chip>}
          </div>
          {/* Schedule summary */}
          <div className="flex items-center gap-2 flex-wrap">
            {d.pre_inspection_date && (
              <Chip color={d.pre_inspection_completed ? "emerald" : "amber"}>
                PRE {fmtDate(d.pre_inspection_date)}{d.pre_inspection_completed ? " ✓" : ""}
              </Chip>
            )}
            {d.adjuster_meeting_date && (
              <Chip color="indigo">Adj {fmtDate(d.adjuster_meeting_date)}</Chip>
            )}
            {d.reinspection_adjuster_meeting_date && (
              <Chip color="purple">2nd Adj {fmtDate(d.reinspection_adjuster_meeting_date)}</Chip>
            )}
          </div>
          {/* Expectation flag */}
          {d.stage === "waiting_on_claims" && (d as Record<string, unknown>).claims_expectation_flag && (
            <Chip color={(d as Record<string, unknown>).claims_expectation_flag === "expected_approval" ? "emerald" : "purple"}>
              {(d as Record<string, unknown>).claims_expectation_flag === "expected_approval" ? "✓ Exp. Approval" : "⟳ Exp. Reinspection"}
            </Chip>
          )}
          {/* Partial approval — flagged reinspection, prioritize to front.
              Gated to the reinspections phase so the badge clears once the
              deal advances into builds/closing (the flag persists as a record). */}
          {d.reinspection_partial_approval && d.phase === "reinspections" && (
            <Chip color="amber">◑ Partial Approval</Chip>
          )}
          {/* Withdraw pending */}
          {(d as Record<string, unknown>).withdraw_status === "pending" && (
            <Chip color="orange">⏳ Withdraw Pending</Chip>
          )}
          {d.claim_notes && (
            <div className="text-xs text-slate-400 truncate italic">
              {d.claim_notes.length > 80 ? d.claim_notes.slice(0, 80) + "…" : d.claim_notes}
            </div>
          )}
          {!["closed_won", "closed_lost", "cancelled"].includes(d.stage ?? "") && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const reason = prompt("Reason for claim denial / cancellation:");
                if (!reason) return;
                setStageUpdating(true);
                try {
                  const { error } = await supabase
                    .from("deals")
                    .update({ stage: "closed_lost", phase: "closed", days_in_stage: 0 })
                    .eq("id", d.id);
                  if (error) throw error;
                  supabase.from("activity_log").insert({
                    deal_id: d.id,
                    activity_type: "stage_change",
                    title: "Claim Denied",
                    description: `Claim denied / cancelled: ${reason}`,
                    metadata: { from_stage: d.stage, from_phase: phase, reason, cancelled_by: "portal" },
                  }).then(() => {});
                  onMutate?.();
                } catch (err: any) {
                  alert(`Update failed: ${err.message ?? err}`);
                } finally {
                  setStageUpdating(false);
                }
              }}
              disabled={stageUpdating}
              className="self-start mt-1 px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {stageUpdating ? "Updating…" : "Claim Denied"}
            </button>
          )}
        </>
      )}

      {/* CompanyCam upload button — reinspections only */}
      {phase === "reinspections" && (
        <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <input
            ref={ccFileRef}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCcUpload([file]);
              if (ccFileRef.current) ccFileRef.current.value = "";
            }}
          />
          <button
            onClick={() => ccFileRef.current?.click()}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setCcDragOver(true); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setCcDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setCcDragOver(false);
              const files = e.dataTransfer.files;
              if (files && files.length > 0) handleCcUpload(Array.from(files));
            }}
            disabled={ccUploading}
            className={
              "text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all disabled:opacity-50 " +
              (ccDragOver
                ? "bg-indigo-500 text-white border-transparent ring-2 ring-offset-1 ring-indigo-500 scale-105"
                : ccUploading
                  ? "bg-indigo-500 text-white border-transparent"
                  : "border-indigo-500 text-indigo-600 hover:bg-indigo-500 hover:text-white")
            }
          >
            {ccUploading ? "Uploading…" : ccDragOver ? "Drop → CompanyCam Report" : "CompanyCam Report"}
          </button>
        </div>
      )}

      {phase === "builds" && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {rsName && <Chip color="teal">RS: {rsName.split(" ")[0]}</Chip>}
            {d.insurance_company && <Chip color="purple">{d.insurance_company}</Chip>}
            {d.claim_number && <Chip color="slate">#{d.claim_number}</Chip>}
            {d.shingle_color && <Chip color="amber">{d.shingle_color}</Chip>}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {(d.commissionable_revenue ?? 0) > 0 && (
              <span className="font-semibold text-bardin-ink">
                {fmtUSD(Number(d.commissionable_revenue), { short: true })} comm
              </span>
            )}
            {d.build_scheduled_date && (
              <Chip color="emerald">Build {fmtDate(d.build_scheduled_date)}</Chip>
            )}
            {d.ho_preferred_build_date && !d.build_scheduled_date && (
              <Chip color="orange">Pref {fmtDate(d.ho_preferred_build_date)}</Chip>
            )}
            {d.material_order_date && <Chip color="slate">Mat ordered</Chip>}
          </div>
          {!["closed_won", "closed_lost", "cancelled"].includes(d.stage ?? "") && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const reason = prompt("Reason for cancelling this build:");
                if (!reason) return;
                setStageUpdating(true);
                try {
                  const { error } = await supabase
                    .from("deals")
                    .update({ stage: "cancelled", phase: "closed", days_in_stage: 0 })
                    .eq("id", d.id);
                  if (error) throw error;
                  supabase.from("activity_log").insert({
                    deal_id: d.id,
                    activity_type: "stage_change",
                    title: "Build Cancelled",
                    description: `Cancelled: ${reason}`,
                    metadata: { from_stage: d.stage, from_phase: "builds", reason, cancelled_by: "portal" },
                  }).then(() => {});
                  onMutate?.();
                } catch (err: any) {
                  alert(`Cancel failed: ${err.message ?? err}`);
                } finally {
                  setStageUpdating(false);
                }
              }}
              disabled={stageUpdating}
              className="self-start mt-1 px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {stageUpdating ? "Updating…" : "Cancel Build"}
            </button>
          )}
        </>
      )}

      {phase === "closing" && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {d.insurance_company && <Chip color="purple">{d.insurance_company}</Chip>}
            {d.claim_number && <Chip color="slate">#{d.claim_number}</Chip>}
          </div>
          {/* Financial summary */}
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {(d.total_contract_amount ?? 0) > 0 && (
              <Detail label="Contract" value={fmtUSD(Number(d.total_contract_amount), { short: true })} />
            )}
            {(d.acv_amount ?? 0) > 0 && (
              <Detail label="ACV" value={fmtUSD(Number(d.acv_amount), { short: true })} />
            )}
            {(d.deductible ?? 0) > 0 && (
              <Detail label="Ded" value={fmtUSD(Number(d.deductible), { short: true })} />
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {(d.pre_build_amount_received ?? 0) > 0 && (
              <span className="text-emerald-600">Pre-build: {fmtUSD(Number(d.pre_build_amount_received), { short: true })}</span>
            )}
            {(d.post_build_amount_received ?? 0) > 0 && (
              <span className="text-emerald-600">Post-build: {fmtUSD(Number(d.post_build_amount_received), { short: true })}</span>
            )}
            {d.receivable_status && <Chip color={d.receivable_status === "paid" ? "emerald" : "orange"}>{d.receivable_status}</Chip>}
          </div>
          {/* Supplement info */}
          {d.supplement_status && d.supplement_status !== "not_needed" && (
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Chip color={d.supplement_approved_date ? "emerald" : "amber"}>
                Supp: {d.supplement_status.replace(/_/g, " ")}
              </Chip>
              {(d.supplement_approved_amount ?? 0) > 0 && (
                <span className="text-slate-500">{fmtUSD(Number(d.supplement_approved_amount), { short: true })}</span>
              )}
              {d.supplement_submitted_to_insurance_date && !d.supplement_approved_date && (
                <span className="text-slate-400">Submitted {fmtDate(d.supplement_submitted_to_insurance_date)}</span>
              )}
            </div>
          )}
          {d.build_completed_date && (
            <Chip color="emerald">Built {fmtDate(d.build_completed_date)}</Chip>
          )}
        </>
      )}

      {/* Stage transition button — shown on every card */}
      <TransitionButton deal={d as Deal} onComplete={onMutate} compact members={members} />

      {/* Call / Text + action row */}
      <div className="flex items-center justify-between pt-1 border-t border-slate-100 mt-1" onClick={(e) => e.stopPropagation()}>
        <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={d.homeowner_phone} />
      </div>

      {/* Action row — ••• button (exec team only: admin, sales_director, asm, director_of_operations / roof_director — or super users) */}
      {(isSuperUser || (!!userRole && MASS_UPDATE_ROLES.includes(userRole))) && (
      <div className="pt-1 border-t border-slate-100 mt-1">
        {!showActions ? (
          <button
            onClick={(e) => { e.stopPropagation(); setShowActions(true); }}
            className="text-[10px] text-slate-400 hover:text-slate-600 transition px-1"
            title="Quick actions"
          >
            •••
          </button>
        ) : (
          <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                defaultValue={d.stage ?? ""}
                onChange={handleStageChange}
                disabled={stageUpdating}
                className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[180px] disabled:opacity-50"
              >
                {ALL_STAGE_OPTIONS.map((group) => (
                  <optgroup key={group.phase} label={group.phase}>
                    {group.stages.map((s) => (
                      <option key={s} value={s}>
                        {STAGE_LABELS[s] ?? s}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {canDeleteDeal && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setShowActions(false); }}
                className="text-xs text-slate-400 hover:text-slate-600 px-1"
              >
                ✕
              </button>
            </div>
            {/* RM / Split RM reassignment */}
            {members && (
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-[10px] text-slate-400">RM:</label>
                <select
                  value={d.owner_id ?? ""}
                  onChange={async (e) => {
                    const val = e.target.value || null;
                    const { error } = await supabase.from("deals").update({ owner_id: val }).eq("id", d.id);
                    if (error) { alert(`RM update failed: ${error.message}`); return; }
                    onMutate?.();
                  }}
                  className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[150px]"
                >
                  <option value="">(none)</option>
                  {Object.entries(members)
                    .sort(([, a], [, b]) => a.full_name.localeCompare(b.full_name))
                    .map(([id, m]) => (
                      <option key={id} value={id}>{m.full_name}</option>
                    ))}
                </select>
                <label className="text-[10px] text-slate-400 ml-1">Split:</label>
                <select
                  value={d.split_rm_id ?? ""}
                  onChange={async (e) => {
                    const val = e.target.value || null;
                    const { error } = await supabase.from("deals").update({ split_rm_id: val }).eq("id", d.id);
                    if (error) { alert(`Split RM update failed: ${error.message}`); return; }
                    onMutate?.();
                  }}
                  className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[150px]"
                >
                  <option value="">(none)</option>
                  {Object.entries(members)
                    .sort(([, a], [, b]) => a.full_name.localeCompare(b.full_name))
                    .map(([id, m]) => (
                      <option key={id} value={id}>{m.full_name}</option>
                    ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// NextStepLine removed — replaced by TransitionButton component
